import {
  APIReferenceType,
  ArdoqId,
  ArrowType,
  LineType,
} from '@ardoq/api-types';
import { GraphEdgeBase } from './GraphEdgeBase';
import { GraphItem, type GraphItemMetadata } from './GraphItem';
import SparkMD5 from 'spark-md5';
import { ItemLabels, SVGColorAttributes, URLFieldValue } from '../types';
import { ComponentGraphEdgeModel } from './types';
import { DiffType, GetCssClassNamesOption } from '@ardoq/data-model';
import { getReferenceTypeSvgStyle } from '@ardoq/renderers';
import { getGraphNodes } from './utils';
import { GraphInterface } from '../graphInterface/graphInterface';
import { RepresentationInterface } from '../representationInterface/representationInterface';
import {
  DEFAULT_EMPTY_ITEM_LABELS,
  DIFF_TYPE,
  IS_PARENT_OF,
  PARENT_CHILD_REFERENCE,
} from '@ardoq/global-consts';
import { colors } from '@ardoq/design-tokens';
import {
  DEFAULT_EDGE_COLOR_ATTRIBUTES,
  PARENT_CHILD_LINE_TYPE,
} from './consts';

export type GraphEdgeMetadata = {
  modelTypeColor: string;
  globalTypeId: string | null;
  visualDiffType: DiffType;
  itemLabels: ItemLabels;
  urlFieldValues: URLFieldValue[] | null;
  cssClassNames: string;
  lineBeginning: ArrowType;
  lineEnding: ArrowType;
  line: LineType;
  isReference: boolean;
  isAggregatedParentChildReference?: boolean;
  cssColors: SVGColorAttributes;
};

type GraphEdgeConstructorArgsBase = {
  sourceId: string;
  targetId: string;
  sourceModelId: string;
  targetModelId: string;
  id?: string;
  metadata: GraphItemMetadata & GraphEdgeMetadata;
};

type SingleReferenceConstructorArgs = GraphEdgeConstructorArgsBase & {
  modelId: string;
  isAggregated: false;
  modelIds?: never;
};

type AggregatedConstructorArgs = GraphEdgeConstructorArgsBase & {
  /**
   * The joined ids of aggregated references.
   */
  modelId: string;
  /**
   * The model ids of aggregated references represented through this edge.
   */
  modelIds: string[];
  isAggregated: true;
};

type GraphEdgeConstructorArgs =
  | SingleReferenceConstructorArgs
  | AggregatedConstructorArgs;

type CreateArgsBase = {
  id?: string;
  sourceId: string;
  targetId: string;
  graphNodeMap: Map<string, GraphItem>;
};

type CreateArgs = CreateArgsBase & {
  modelId: string;
  metadata: GraphEdgeMetadata & Pick<GraphItemMetadata, 'isReference'>;
};

type CreateAggregatedArgs = CreateArgsBase & {
  modelIds: string[];
  metadata: GraphEdgeMetadata;
};

type GetComponentGraphEdgeMetadataArgs = {
  viewInterface: GraphInterface & RepresentationInterface;
  referenceModelId: ArdoqId;
  referenceModelType: APIReferenceType;
  edgeId: string;
  edgeLabelsData: Map<string, ItemLabels>;
  urlFieldValuesByReferenceId: Map<string, URLFieldValue[] | null>;
  edgeColorsByReferenceModelId: Map<string, SVGColorAttributes>;
};

/**
 * Gets metadata for a component graph edge based on its reference model.
 * If the reference model type is not found in the view interface, the metadata
 * will use default values (default colors, line types, and arrow types).
 */
export const getComponentGraphEdgeMetadata = ({
  viewInterface,
  referenceModelId,
  referenceModelType,
  edgeId,
  edgeLabelsData,
  urlFieldValuesByReferenceId,
  edgeColorsByReferenceModelId,
}: GetComponentGraphEdgeMetadataArgs): GraphEdgeMetadata => {
  const itemLabels = edgeLabelsData.get(edgeId) ?? DEFAULT_EMPTY_ITEM_LABELS;

  const visualDiffType =
    viewInterface.getReferenceAttribute(referenceModelId, DIFF_TYPE) ??
    DiffType.NONE;

  // Handle cardinality: if the reference type has cardinality enabled,
  // use the actual cardinality values from the reference instance.
  const lineBeginning = referenceModelType.hasCardinality
    ? ((viewInterface.getReferenceAttribute(
        referenceModelId,
        'sourceCardinality'
      ) as ArrowType | null) ??
      referenceModelType.lineBeginning ??
      ArrowType.NONE)
    : (referenceModelType.lineBeginning ?? ArrowType.NONE);

  const lineEnding = referenceModelType.hasCardinality
    ? ((viewInterface.getReferenceAttribute(
        referenceModelId,
        'targetCardinality'
      ) as ArrowType | null) ?? referenceModelType.lineEnding)
    : referenceModelType.lineEnding;

  return {
    globalTypeId:
      viewInterface.getGlobalReferenceTypeId(referenceModelId) ?? '',
    isReference: viewInterface.isReference(referenceModelId),
    modelTypeColor: referenceModelType.color ?? colors.black950,
    visualDiffType,
    itemLabels,
    urlFieldValues: urlFieldValuesByReferenceId.get(referenceModelId) ?? null,
    cssClassNames:
      viewInterface.getReferenceCssClassNames(referenceModelId) ?? '',
    lineBeginning,
    lineEnding,
    line: referenceModelType.line || LineType.SOLID,
    cssColors:
      edgeColorsByReferenceModelId.get(referenceModelId) ??
      DEFAULT_EDGE_COLOR_ATTRIBUTES,
  };
};

type GetAggregatedComponentGraphEdgeMetadataArgs = Omit<
  GetComponentGraphEdgeMetadataArgs,
  'referenceModelId'
> & {
  referenceModelIds: string[];
  isParentChildReference?: boolean;
};

/**
 * Gets metadata for an aggregated reference edge (multiple references between same nodes).
 */
export const getAggregatedComponentGraphEdgeMetadata = ({
  viewInterface,
  referenceModelIds,
  referenceModelType: firstReferenceModelType,
  edgeId,
  edgeLabelsData,
  urlFieldValuesByReferenceId,
  edgeColorsByReferenceModelId,
  isParentChildReference = false,
}: GetAggregatedComponentGraphEdgeMetadataArgs): GraphEdgeMetadata | null => {
  if (isParentChildReference) {
    return {
      globalTypeId: null,
      isReference: false,
      modelTypeColor: '#000',
      visualDiffType: DiffType.NONE,
      itemLabels: {
        mainLabel: IS_PARENT_OF,
      },
      urlFieldValues: null,
      cssClassNames: PARENT_CHILD_REFERENCE,
      lineBeginning: ArrowType.NONE,
      lineEnding: ArrowType.DIAMOND,
      line: PARENT_CHILD_LINE_TYPE,
      isAggregatedParentChildReference: isParentChildReference,
      cssColors: DEFAULT_EDGE_COLOR_ATTRIBUTES,
    };
  }

  // Use first reference type for representation. the references are
  // aggregated by the getDistinctReferenceKey in aggregateReferences
  const firstReferenceModelId = referenceModelIds?.[0];

  if (!firstReferenceModelId) {
    return null;
  }

  const firstReferenceMetadata = getComponentGraphEdgeMetadata({
    viewInterface,
    referenceModelId: firstReferenceModelId,
    referenceModelType: firstReferenceModelType,
    edgeId,
    edgeLabelsData,
    urlFieldValuesByReferenceId,
    edgeColorsByReferenceModelId,
  });

  // For aggregated edges, when cardinality is enabled, always use ArrowType.NONE
  // (matching referenceInterface.getLineBeginning behavior)
  const lineBeginning = firstReferenceModelType.hasCardinality
    ? ArrowType.NONE
    : firstReferenceMetadata.lineBeginning;

  const lineEnding = firstReferenceModelType.hasCardinality
    ? ArrowType.NONE
    : firstReferenceMetadata.lineEnding;

  return {
    ...firstReferenceMetadata,
    isReference: false,
    lineBeginning,
    lineEnding,
  };
};

export class ComponentGraphEdge
  extends GraphEdgeBase
  implements ComponentGraphEdgeModel
{
  static create({
    id,
    modelId,
    sourceId,
    targetId,
    graphNodeMap,
    metadata,
  }: CreateArgs) {
    const { sourceGraphNode, targetGraphNode } = getGraphNodes(
      sourceId,
      targetId,
      graphNodeMap
    );

    const withBaseClassData: GraphItemMetadata & GraphEdgeMetadata = {
      isGroup: false,
      isComponent: false,
      ...metadata,
    };

    return new ComponentGraphEdge({
      id,
      modelId,
      isAggregated: false,
      sourceId: sourceGraphNode.id,
      targetId: targetGraphNode.id,
      sourceModelId: sourceGraphNode.modelId,
      targetModelId: targetGraphNode.modelId,
      metadata: withBaseClassData,
    });
  }

  static createAggregated({
    id,
    modelIds,
    sourceId,
    targetId,
    graphNodeMap,
    metadata,
  }: CreateAggregatedArgs) {
    const { sourceGraphNode, targetGraphNode } = getGraphNodes(
      sourceId,
      targetId,
      graphNodeMap
    );

    const withBaseClassData: GraphItemMetadata & GraphEdgeMetadata = {
      isGroup: false,
      isComponent: false,
      ...metadata,
    };

    return new ComponentGraphEdge({
      id,
      modelId: modelIds.join(','),
      modelIds,
      isAggregated: true,
      sourceId: sourceGraphNode.id,
      targetId: targetGraphNode.id,
      sourceModelId: sourceGraphNode.modelId,
      targetModelId: targetGraphNode.modelId,
      metadata: withBaseClassData,
    });
  }

  protected metadata: GraphEdgeMetadata;
  readonly source: string;
  readonly target: string;
  readonly sourceModelId: string;
  readonly targetModelId: string;
  public readonly modelIds?: string[];
  public readonly isParentChildReference: boolean;
  public readonly isAggregated: boolean;

  private constructor({
    modelId,
    modelIds,
    isAggregated,
    sourceId,
    targetId,
    sourceModelId,
    targetModelId,
    id,
    metadata,
  }: GraphEdgeConstructorArgs) {
    super(modelId, metadata, id);
    this.source = sourceId;
    this.target = targetId;
    this.sourceModelId = sourceModelId;
    this.targetModelId = targetModelId;
    this.metadata = metadata;
    this.isParentChildReference = Boolean(
      metadata.isAggregatedParentChildReference
    );

    this.isAggregated = isAggregated;
    if (isAggregated) {
      this.modelIds = modelIds ?? [];
    }

    this.hash = SparkMD5.hash(this.getRawHash());
  }

  override getLabel(): string {
    return this.metadata.itemLabels.mainLabel || this.label || '';
  }

  override getVisualDiffType(): DiffType {
    return this.metadata.visualDiffType;
  }

  override getCSS(_?: GetCssClassNamesOption): string {
    return this.metadata.cssClassNames;
  }

  override hasURLFields(): boolean {
    return Boolean(this.metadata.urlFieldValues?.length);
  }

  override getItemLabels(): ItemLabels {
    return this.metadata.itemLabels;
  }

  override setItemLabels(itemLabels: ItemLabels): void {
    this.metadata.itemLabels = itemLabels;
  }

  getGlobalTypeId() {
    return this.metadata.globalTypeId;
  }

  getUrlFieldValues() {
    return this.metadata.urlFieldValues;
  }
  setUrlFieldValues(urlFieldValues: URLFieldValue[] | null) {
    this.metadata.urlFieldValues = urlFieldValues;
  }

  setMetaData(metadata: GraphEdgeMetadata) {
    this.metadata = metadata;
  }

  getMetadata() {
    return this.metadata;
  }

  getLineBeginning(): ArrowType {
    return this.metadata.lineBeginning;
  }

  getLineEnding(): ArrowType {
    return this.metadata.lineEnding;
  }

  getLine(): LineType {
    return this.metadata.line;
  }

  getModelTypeColor(): string {
    return this.metadata.modelTypeColor;
  }
  getStyle() {
    return getReferenceTypeSvgStyle(this.getLine(), this.getModelTypeColor());
  }

  getCssColorAttributes(): SVGColorAttributes {
    return this.metadata.cssColors;
  }

  setCssColorAttributes(cssColors: SVGColorAttributes): void {
    this.metadata.cssColors = cssColors;
  }

  protected getRawHash() {
    return `${super.getRawHash()}${this.getLineBeginning()}${this.getLineEnding()}${
      this.sourceModelId
    }${this.targetModelId}`;
  }
}
